import {
  AddressInput,
  Basket,
  BasketDiscountType,
  Checkout,
  CheckoutStatus,
  Deal,
  PaymentCurrencyType,
  PaymentOrderStatus,
  PaymentProvider,
  PaymentStatus,
  WalletEntityType,
  BasketItemType,
  MultiCurrencyDetailsInput,
  Wallet,
  Query,
  QueryWalletArgs
} from "@irati/types"
import authentication from "@irati/auth"
import {ClientError} from "@irati/utils/errors"
import paymentService from "@irati/payments/services/Payments"
import walletService from "@irati/wallet/graph/wallet"
import stocksService from "@irati/stock/services/stocks"
import tracking from "@irati-tracking/perks/perks"
import CurrencyService from "@irati/currency/services/currency"
import client from "@irati/utils/services/apollo"
import {isFlexiCurrency, isRewardCurrency} from "@shared/wallet"
import {PointsBlendBreakdown, calculatePointsBreakdown} from "@shared/checkout/utils/basket/calculatePointsBlendBreakdown"
import walletQuery from "@irati/wallet/graph/query/wallet"
import basketService from "../../services/basket"
import basket from "../basket"
import checkoutService from "../../services/checkout"
import Screen from "./enum/Screen"
import handleNoProduct, {isNoProduct} from "./flows/handleNoProduct"
import handleSingleProduct, {isSingleProduct} from "./flows/handleSingleProduct"
import checkoutError, {CheckoutError} from "./helpers/checkoutError"

const EXCHANGE_RATE = 1.0

export interface Details {
  screen: Screen
  deal: Deal
  checkoutId?: string
  redemptionId?: string
  paymentOrderId?: string
  source?: string
}

class RedemptionFlow {
  async selectDeal(deal: Deal): Promise<Details> {
    if (isNoProduct(deal)) {
      return handleNoProduct(deal)
    }

    // Check if deal has price and try to find the cheapest available
    const dealMinPrice = deal.productConfig?.limits?.forOpenValue?.minValue || deal.productConfig?.products[0]?.salePrice?.value || 0
    const dealCurrency = deal.productConfig?.products[0]?.salePrice?.currency
    const costInCents = dealMinPrice * 100

    await Promise.all(
      [
        // Check stock count if deal has products attached
        this.checkStockCount(deal),

        // Run balance check against cheapest product for Rewards or FlexiPerks
        isFlexiCurrency(dealCurrency) && isFlexiBalanceEnough(costInCents, dealCurrency)
      ].filter(Boolean)
    )

    if (isSingleProduct(deal)) {
      return handleSingleProduct(deal)
    }

    return {screen: Screen.DealSelectProducts, deal}
  }

  async selectProducts(deal: Deal): Promise<Details> {
    const isRewards = isRewardCurrency(deal.productConfig?.products[0]?.salePrice?.currency)
    const userId = user()

    basket.items.forEach((item) => {
      item.targetUserId = userId
      item.amount = item.openValue
      item.entityType = isRewards ? BasketItemType.FlexiReward : BasketItemType.Deal
    })

    const {total: {currency = "", amount = 0} = {}} = await basketService.updateBasket({
      items: basket.items,
      userId,
      paymentDetails: {
        provider: basket.paymentProvider,
        discountType: basket.isCashback ? BasketDiscountType.Cashback : BasketDiscountType.Standard
      }
    })

    await Promise.all(
      [
        isFlexiCurrency(currency) && isFlexiBalanceEnough(amount, currency), //
        basketService.canRedeemBasket(userId, {fetchPolicy: "no-cache"})
      ].filter(Boolean)
    )

    if (amount > 0) {
      // checkoutService.startCheckout is handled by containers/DealPayment line 57
      return {screen: Screen.DealPayment, deal}
    }

    const checkout = await checkoutService.startCheckout(userId)
    const completeCheckout = await checkoutService.completeCheckout(checkout.uuid!)
    const redemptionId = completeCheckout.fullRedemptions[0].uuid
    return {screen: Screen.DealSuccess, deal, checkoutId: checkout.uuid, redemptionId}
  }

  setShippingAddress(shippingAddress: AddressInput): Promise<Basket> {
    // @ts-expect-error this is fine
    basket.shippingAddress = shippingAddress
    return basketService.updateBasket({
      items: basket.items,
      userId: user(),
      shippingAddress
    })
  }

  async paymentComplete(deal: Deal, checkoutId: string, paymentOrderId: string): Promise<Details> {
    try {
      const payment = await paymentService.completePayment({paymentOrderId})
      if (payment.status === PaymentStatus.Failed) {
        throw new Error()
      }
      const maybeWalletPayment = await makeWalletPaymentIfRequired(paymentOrderId, deal, checkoutId)

      const checkout = await checkoutService.completeCheckout(checkoutId)
      if (checkout.status !== CheckoutStatus.Completed) {
        throw new Error()
      }

      if (maybeWalletPayment) {
        const {remaining, RPWallet, TTWallet} = maybeWalletPayment

        // once all payments are complete we update the client side balances. There
        // is a delay in propagating the balances so the alternative would be
        // polling, but we know what the value will be
        updateApolloCacheBalance(TTWallet, remaining.TT.amount)
        updateApolloCacheBalance(RPWallet, remaining.RP.amount)
      }
      tracking.redeemPerkCompleteCheckout(
        deal,
        checkout!.paymentOrder?.amount!,
        checkout!.paymentOrder?.currency!,
        getWalletAmountUsed(checkout)
      )
      const redemptionId = checkout.fullRedemptions[0].uuid
      return {screen: Screen.DealSuccess, deal, redemptionId, checkoutId}
    } catch (e) {
      const {reason, checkout} = await checkoutError(paymentOrderId, checkoutId)
      tracking.checkoutFailure(getTrackingData(reason, paymentOrderId, checkout))
      return {screen: Screen.DealRedemptionError, deal, checkoutId, paymentOrderId}
    }
  }

  private async checkStockCount(deal: Deal) {
    const {allOutOfStock} = await stocksService.stockCount(deal.uuid!)
    if (allOutOfStock) {
      throw new Error("perks.containers.DealImportantInfoContainer.noStock.error")
    }
  }
}

const redemptionFlow = new RedemptionFlow()

export default redemptionFlow

async function isFlexiBalanceEnough(costInCents: number, currency: PaymentCurrencyType): Promise<void> {
  const {isPointsBlendEligible} = getWalletPermissions()

  const {RPWallet, TTWallet} = await fetchFlexiWallets(user())
  const {canAfford, isBlendPayment, amountShort} = calculatePointsBreakdown({
    costInCents,
    currency,
    RPWallet,
    TTWallet,
    isPointsBlendEligible
  })

  if (!canAfford || (isBlendPayment && !isPointsBlendEligible)) {
    if (isPointsBlendEligible) {
      throw new ClientError("perks.containers.DealImportantInfoContainer.notEnoughWalletBalance.pointsBlendError", {
        total: costInCents / 100,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        missing: amountShort! / 100
      })
    } else {
      throw new Error("perks.containers.DealImportantInfoContainer.notEnoughWalletBalance.error")
    }
  }
}

async function makeWalletPaymentIfRequired(
  paymentOrderId: string,
  deal: Deal,
  checkoutId: string
): Promise<undefined | Prettify<Pick<PointsBlendBreakdown, "spent" | "remaining"> & {TTWallet: Wallet; RPWallet: Wallet}>> {
  const po = await paymentService.getPaymentOrder(paymentOrderId, {forceRefetch: true})

  if (po.status !== PaymentOrderStatus.Pending) return

  // get any unpaid balance left on the paymentOrder - at present this is only
  // applicable if the user is redeeming a cash purchase and have already
  // made a payment by card. Else if it's a flexi purchase, the outstanding
  // balance will be the full price
  const outstanding =
    po?.payments?.reduce(
      (paid, {status, amount = 0}) =>
        status === "COMPLETED" //
          ? paid - amount
          : paid,
      po.amount || 0
    ) || 0

  if (outstanding <= 0) return

  const currency = po.currency

  if (!currency) throw new Error("Impossible, payment order must have a currency")

  // cash wallet payment
  if (!isFlexiCurrency(currency)) {
    const cashWallet = await walletService.get(user(), currency, WalletEntityType.User, {fetchPolicy: "network-only"})
    await paymentService.payByUserWallet(po, cashWallet, {amount: outstanding}, deal, checkoutId)
    // update client side balance
    await walletService.get(user(), currency, WalletEntityType.User, {fetchPolicy: "network-only"})
    return
  }

  const [{TTWallet, RPWallet}] = await Promise.all([fetchFlexiWallets(user()), validateExchangeRate()])

  const {isPointsBlendEligible} = getWalletPermissions()

  const {spent, remaining, canAfford, isBlendPayment} = calculatePointsBreakdown({
    currency,
    costInCents: outstanding,
    TTWallet,
    RPWallet,
    isPointsBlendEligible
  })

  // This shouldn't happen, but just in case we still check.
  // At this point we should have already checked this is just a flexi-payment,
  // and no other payments have been made
  if (!canAfford || (isBlendPayment && !isPointsBlendEligible)) {
    throw new Error("perks.containers.DealImportantInfoContainer.notEnoughWalletBalance.error")
  }

  const {RP, TT} = spent

  if (isBlendPayment) {
    const multiCurrencyDetails = (equivalentAmount: number): MultiCurrencyDetailsInput => ({
      exchangeRate: EXCHANGE_RATE,
      orderCurrency: currency,
      equivalentAmount
    })

    // these must be done in sequence to allow payments to check what has already been paid
    if (TT.amount > 0) {
      await paymentService.payByUserWallet(po, TTWallet, {amount: TT.amount}, deal, checkoutId, multiCurrencyDetails(TT.amount))
    }
    if (RP.amount > 0) {
      await paymentService.payByUserWallet(po, RPWallet, {amount: RP.amount}, deal, checkoutId, multiCurrencyDetails(RP.amount))
    }
  } else if (TT.amount > 0) {
    await paymentService.payByUserWallet(po, TTWallet, {amount: TT.amount}, deal, checkoutId)
  } else if (RP.amount > 0) {
    await paymentService.payByUserWallet(po, RPWallet, {amount: RP.amount}, deal, checkoutId)
  } else {
    // should never happen
    throw new Error("Something went wrong during payments")
  }

  return {remaining, spent, TTWallet, RPWallet}
}

async function fetchFlexiWallets(uuid: string) {
  const [RPWallet, TTWallet] = await Promise.all([
    walletService.get(uuid, PaymentCurrencyType.Rp, WalletEntityType.User, {fetchPolicy: "network-only"}),
    walletService.get(uuid, PaymentCurrencyType.Tt, WalletEntityType.User, {fetchPolicy: "network-only"})
  ])
  return {RPWallet, TTWallet}
}

async function validateExchangeRate() {
  const exchangeRate = await CurrencyService.getFPCashbackForCurrency(PaymentCurrencyType.Tt).then(({rpExchangeRate}) => rpExchangeRate)

  if (exchangeRate !== EXCHANGE_RATE) {
    // this should never happen (famous last words)
    // There are a great many assumptions on FP == RP, if they don't
    // something has gone wrong or been missed and we need to stop and
    // reconsider things
    throw new Error("Invalid Exchange rate between Perkbox currencies")
  }
}

function getWalletPermissions() {
  const hasTTWallet = authentication.apps("wallet.treatTokens")
  const hasRPWallet = authentication.apps("wallet.flexiRewards")
  const isEligible = !!authentication.apps()?.wallet?.pointsBlendEligible
  const isPointsBlendEligible = !!(isEligible && hasRPWallet && hasTTWallet)
  return {isPointsBlendEligible}
}

function getWalletAmountUsed(checkout: Checkout) {
  const walletPayments = checkout.paymentOrder?.payments?.filter((p) => p.provider === PaymentProvider.Wallet) || []
  return walletPayments.map((p) => p.amount).reduce((a = 0, c = 0) => a + c, 0) || 0
}

function getTrackingData(reason: CheckoutError, paymentOrderId: string, checkout?: Checkout) {
  return {
    reason,
    user_id: authentication.user().uuid,
    tenant_id: authentication.userTenant().uuid,
    payment_id: checkout?.paymentOrder?.payments?.map((payment) => payment.uuid).join(","),
    order_id: paymentOrderId,
    amount: checkout?.paymentOrder?.amount,
    currency: checkout?.paymentOrder?.currency,
    provider: checkout?.paymentOrder?.payments?.map((payment) => payment.provider).join(","),
    country_code: checkout?.paymentOrder?.countryCode,
    deal_id: checkout?.basket?.items?.map((item) => item.deal?.uuid).join(","),
    deal_header: checkout?.basket?.items?.map((item) => item.deal?.header).join(",")
  }
}

function user() {
  return authentication.user().uuid || ""
}

function updateApolloCacheBalance(wallet: Wallet, amount: number) {
  type CachedWallet = Pick<Query, "wallet">
  const walletArgs: QueryWalletArgs = {
    userId: wallet.ownerUuid!,
    currency: wallet.currency,
    entityType: WalletEntityType.User,
    entityId: wallet.ownerUuid
  }
  const cache =
    client.readQuery<CachedWallet>({
      query: walletQuery,
      variables: walletArgs
    }) || {}

  if (!cache.wallet) {
    console.warn("no wallet in cache, likely incorrect variables")
    return
  }

  client.writeQuery<CachedWallet>({
    query: walletQuery,
    variables: walletArgs,
    data: {
      wallet: {
        ...cache.wallet,
        amount,
        availableBalance: amount / 100,
        availableBalanceInCents: amount
      }
    }
  })
}
